import { useAuth } from "react-oidc-context";
import { BrowserRouter, Navigate, Route, Routes, useParams } from "react-router-dom";

import { BetaGrants, FleetEvidence, HitlGates, WelcomeContent } from "./pages/labeeb/surfaces";
import { Layout } from "./components/Layout";
import { REDIRECTS } from "./nav/routes";
import type { AppConfig } from "./config";
import { SignIn } from "./pages/SignIn";
import { Audit } from "./pages/governance/Audit";
import { Permissions } from "./pages/governance/Permissions";
import { FleetPosture } from "./pages/overview/FleetPosture";
import { Caps } from "./pages/policy/Caps";
import { CapsTier } from "./pages/policy/CapsTier";
import { Flags } from "./pages/policy/Flags";
import { Tiers } from "./pages/policy/Tiers";
import { Access } from "./pages/tenants/Access";
import { Tenant } from "./pages/tenants/Tenant";
import { TenantHistory } from "./pages/tenants/TenantHistory";
import { TenantUsage } from "./pages/tenants/TenantUsage";
import { TenantUser } from "./pages/tenants/TenantUser";
import { TenantUsers } from "./pages/tenants/TenantUsers";
import { Tenants } from "./pages/tenants/Tenants";
import { Button } from "./f7";

export function App({ config }: { config: AppConfig }) {
  const auth = useAuth();

  // NO signinSilent() here, and that is deliberate.
  //
  // Silent renew loads the authorize endpoint in a hidden iframe and depends on
  // Cognito's session cookie, which is THIRD-PARTY relative to this origin.
  // Browsers block it, so the frame hangs until oidc-client-ts times it out —
  // on the path EVERY signed-out visitor takes. Going straight to the sign-in
  // screen renders instantly and costs one click.

  if (auth.isLoading) {
    return (
      <div className="flex min-h-full items-center justify-center">
        <p className="text-sm text-slate-500 dark:text-slate-400">Signing in…</p>
      </div>
    );
  }

  if (auth.error) {
    return (
      <div className="flex min-h-full items-center justify-center p-6">
        <div className="w-full max-w-md rounded-md border border-slate-950/10 dark:border-white/10 bg-white dark:bg-slate-900 p-6 text-center shadow-lg">
          <h1 className="text-lg font-semibold">Sign-in failed</h1>
          <p className="mt-2 text-sm text-red-600 dark:text-red-400">{auth.error.message}</p>
          <Button onClick={() => void auth.signinRedirect()} className="mt-4">
            Try again
          </Button>
        </div>
      </div>
    );
  }

  if (!auth.isAuthenticated) return <SignIn />;

  return (
    <BrowserRouter>
      <Layout config={config}>
        <Routes>
          {/* Overview */}
          <Route path="/" element={<FleetPosture config={config} />} />

          {/* Tenants. Static paths BEFORE :ref — otherwise "access" can match
              as a business reference and render a merchant page for a merchant
              that does not exist, which surfaces as an API 404 and reads like a
              backend fault. Pinned by nav/routes.test.ts. */}
          <Route path="/tenants" element={<Tenants config={config} />} />
          <Route path="/tenants/access" element={<Access config={config} />} />
          <Route
            path="/tenants/evidence"
            element={<FleetEvidence config={config} />}
          />
          <Route path="/tenants/:ref" element={<Tenant config={config} />} />
          <Route path="/tenants/:ref/usage" element={<TenantUsage config={config} />} />
          <Route path="/tenants/:ref/history" element={<TenantHistory config={config} />} />
          <Route path="/tenants/:ref/users" element={<TenantUsers config={config} />} />
          <Route path="/tenants/:ref/users/:email" element={<TenantUser config={config} />} />

          {/* Policy */}
          {/* Tiers & credits is mostly Labeeb's — the envelopes are messages,
              tokens and Snowflake queries per tier, none of which MCP exposes.
              MCP's own per-tier view is Tool caps, next door. Pointing this at
              Caps would render the same page under two URLs and light the
              wrong tab. */}
          <Route path="/policy/tiers" element={<Tiers config={config} />} />
          <Route path="/policy/caps" element={<Caps config={config} />} />
          <Route path="/policy/caps/:tier" element={<CapsTier config={config} />} />
          <Route
            path="/policy/beta"
            element={<BetaGrants config={config} />}
          />
          <Route path="/policy/flags" element={<Flags config={config} />} />
          <Route
            path="/policy/hitl"
            element={<HitlGates />}
          />
          <Route
            path="/policy/welcome"
            element={<WelcomeContent />}
          />

          {/* Governance */}
          <Route path="/governance/permissions" element={<Permissions config={config} />} />
          <Route path="/governance/audit" element={<Audit config={config} />} />

          {/* Every URL this console has ever served still resolves. */}
          {REDIRECTS.map((r) => (
            <Route key={r.from} path={r.from} element={<Redirect to={r.to} />} />
          ))}

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Layout>
    </BrowserRouter>
  );
}


/**
 * A redirect that carries its params across.
 *
 * The previous version read `window.location.pathname` and split on "/", which
 * mangled any reference needing URL-encoding and bypassed the router entirely.
 * Reading `useParams` and re-encoding means the old URL and the new one agree
 * about what the reference actually is.
 */
function Redirect({ to }: { to: string }) {
  const params = useParams();
  const path = to.replace(/:(\w+)/g, (_, key: string) =>
    encodeURIComponent(params[key] ?? ""),
  );
  return <Navigate to={path} replace />;
}
